// src/core/tribuna.js — capa de datos de la Tribuna Pública.
//
// FASE 4 · F1.3.d2. La tribuna es el feed público agregador de rituales:
// `tribuna_feed` (una fila por ritual público, con su `score` server-side).
//
// `tribuna_feed` lo pueblan los triggers (F1.3.d2): `fn_on_ritual_creado`
// inserta el ritual público al crearlo (score baseline 0); `fn_on_reaccion_creada`
// sube el score con cada reacción real; el cron `job_mantenimiento` lo limpia
// (rituales bajados por "al pasto" o vencidos). El algoritmo de scoring fino
// "por tipo" queda como DT (tribuna baseline).
//
// La lectura del feed pasa por el RPC `feed_tribuna` (`SECURITY DEFINER`):
// resuelve el join `tribuna_feed → ritual → jugadores → grupos` sorteando la
// RLS de `grupos` (que ocultaría el nombre de los grupos ajenos) y agrega los
// contadores de reacción. Tiene `GRANT` a `anon` — la tribuna se ve sin login.
//
// FASE 4 · DT-BI (parpadeo Tribuna): cache en memoria con TTL 60s + dedup de
// requests en vuelo (mismo patrón `memoConTTL` que core/rituales.js). Evita
// refetch en navegación corta entre vistas de tribuna. F1.4.b (Realtime)
// reemplaza la invalidación TTL por push.

import { supabase } from '../supabase.js'
import { withTimeout, queryWithTimeout } from '../utils/timeout.js'

const TRIBUNA_CACHE_TTL_MS = 60_000

// Auditoría boot-hang · DT-H3 (5-jun-2026): los RPC de la tribuna no tenían
// timeout — con red lenta el feed colgaba la vista para siempre. Tope por RPC:
// abort real del fetch (queryWithTimeout) + cap duro externo (withTimeout) por
// si el abort no liberase. El feed PROPAGA el error (la vista muestra
// "Reintentar"); los contadores/flag degradan a default (son decorativos).
const TRIBUNA_RPC_TIMEOUT_MS = 12000
const rpcTribuna = (label, buildRpc) =>
  withTimeout(
    queryWithTimeout((signal) => buildRpc().abortSignal(signal), TRIBUNA_RPC_TIMEOUT_MS, label),
    TRIBUNA_RPC_TIMEOUT_MS + 1000, label,
  )

const memoConTTL = (ttl) => {
  const cache = new Map()
  const inflight = new Map()
  return {
    run: async (clave, fn) => {
      const hit = cache.get(clave)
      if (hit && Date.now() - hit.at < ttl) return hit.value
      if (inflight.has(clave)) return inflight.get(clave)
      const promise = (async () => {
        try {
          const value = await fn()
          cache.set(clave, { value, at: Date.now() })
          return value
        } finally {
          inflight.delete(clave)
        }
      })()
      inflight.set(clave, promise)
      return promise
    },
    clear: () => { cache.clear(); inflight.clear() },
  }
}

const _feedMemo = memoConTTL(TRIBUNA_CACHE_TTL_MS)

// Invalida la caché completa del feed. Útil tras una mutación que afecte la
// tribuna. Hoy no se llama desde acá — las mutaciones viven en core/rituales.js;
// con Realtime (F1.4.b) la invalidación pasa a ser por push.
export const invalidarCacheTribuna = () => _feedMemo.clear()

// Ventana de la muralla de arengas: cuántas del ranking (score DESC) alimentan
// la Tribuna, y a cuántas se rellena repitiendo si hay menos reales. ÚNICO lugar
// configurable. OJO: el DOM efectivo de la muralla ≈ N × 3 (triplicado) × 12
// (carriles); si la muralla traba, bajar este número. El clamp del RPC
// feed_tribuna admite hasta 500.
export const TRIBUNA_WINDOW = 500

// Cuántas cards pinta CADA carril de la muralla. perf-tribuna mobile (31-may):
// bajado a 6 (de 8) para aliviar al celu y que el strip (6×3 = 2.803px) quede
// DEBAJO del límite de textura mobile (~4096px) → el GPU no tilea la capa. La
// velocidad px/s la fija `--duration` en el módulo de la vista (rango aplanado
// ~58..39 px/s). 12 filas × 6 = 72 cards visibles.
export const TRIBUNA_LANE_CARDS = 6

// Cuántas de las 72 visibles son DESTACADAS (top del score C). Las demás
// (72 − 24 = 48) las aporta el bombo. Ver src/core/tribuna-vidriera.js.
export const TRIBUNA_DESTACADOS = 24

// Renovación de la vidriera (selección visible). perf-tribuna vidriera (31-may):
// bajado de 5 a 2 min → como la vidriera siempre incluye lo de arriba (que C
// sube), una arenga nueva aparece a más tardar en la próxima renovación (~2 min)
// una vez que C la subió (el recompute de C sigue cada 5 min, sin cambios).
export const TRIBUNA_REFRESCO_MS = 2 * 60 * 1000

// Rellena la muralla a N: si hay >= N reales, recorta a las primeras N (ya
// vienen ordenadas por score). Si hay menos, cicla las reales hasta N para que
// nunca se vea vacío ni corte el loop (mismo criterio que el triplicado,
// parametrizado a N). Las repetidas son la MISMA referencia de arenga real →
// el data-id queda intacto y el click/abrir resuelve la arenga correcta.
export const rellenarHastaN = (arengas, n) =>
  (!arengas.length || arengas.length >= n)
    ? arengas.slice(0, n)
    : Array.from({ length: n }, (_, i) => arengas[i % arengas.length])

// Feed público de un tipo de ritual ('arenga' | 'cabala' | 'promesa'),
// ordenado por `score` (server-side) y paginado. Cada item trae los campos del
// ritual + `feed_score`, `creador_nombre`, `grupo_nombre` y `reacciones`
// (mapa { tipo_reaccion: count }). Cacheado por clave `tipo|limit|offset`.
export const getFeedTribuna = async (ritualTipo, { limit = 30, offset = 0 } = {}) => {
  return _feedMemo.run(`${ritualTipo}|${limit}|${offset}`, async () => {
    // El feed es el contenido principal: si falla/timeoutea, PROPAGA el error
    // (memoConTTL no cachea un throw → el próximo intento reintenta) para que la
    // vista muestre "Reintentar" en vez de quedar en spinner o "vacío" engañoso.
    const { data, error } = await rpcTribuna('feed_tribuna', () =>
      supabase.rpc('feed_tribuna', { p_ritual_tipo: ritualTipo, p_limit: limit, p_offset: offset }))
    if (error) { console.error('[tribuna] getFeedTribuna:', error.message); throw new Error(`feed_tribuna: ${error.message}`) }
    return data || []
  })
}

// ─── Contadores secundarios de la Tribuna (F1.4.b · resuelven DT-BX/DT-BY) ──
// Cacheados por TTL corto en el mismo memo del feed (clave separada).

// Cuenta TODAS las arengas creadas (públicas + privadas, bajadas o no) — el
// banner de la Tribuna muestra "números grandes" (criterio dueño 30-may): el
// universo más amplio, sin filtros. Antes el banner usaba publicArengas.length
// (= filas del feed, topeado por el limit) → ahora el conteo real server-side,
// independiente de cuántas filas traiga el feed (60 o 500). RPC SECURITY DEFINER
// (saltea RLS, igual que los otros contadores).
export const contarArengasTotal = async () => {
  return _feedMemo.run('count:arengas_total', async () => {
    try {
      const { data, error } = await rpcTribuna('contar_arengas_total', () => supabase.rpc('contar_arengas_total'))
      if (error) { console.error('[tribuna] contarArengasTotal:', error.message); return 0 }
      return Number(data) || 0
    } catch (err) { console.error('[tribuna] contarArengasTotal timeout:', err?.message || err); return 0 }
  })
}

// Cuenta TODAS las cábalas (criterio "números grandes" 30-may: sin filtro de
// vencidas). DT cosmética: el nombre sigue siendo "activas" pero cuenta todas.
// DT-CX (23-may): delega a RPC SECURITY DEFINER porque la RLS de `cabalas`
// solo deja ver las públicas a anon (filtraba a 0/1 según visibilidad). El
// contador es "prueba social" para todos por igual.
export const contarCabalasActivas = async () => {
  return _feedMemo.run('count:cabalas_activas', async () => {
    try {
      const { data, error } = await rpcTribuna('contar_cabalas_activas', () => supabase.rpc('contar_cabalas_activas'))
      if (error) { console.error('[tribuna] contarCabalasActivas:', error.message); return 0 }
      return Number(data) || 0
    } catch (err) { console.error('[tribuna] contarCabalasActivas timeout:', err?.message || err); return 0 }
  })
}

// Cuenta TODAS las promesas (criterio "números grandes" 30-may: sin filtro de
// estado). DT cosmética: el nombre sigue siendo "activas" pero cuenta todas.
// Mismo razonamiento que contarCabalasActivas: la RLS de `promesas` filtraba
// las privadas para anon. RPC SECURITY DEFINER iguala el contador.
export const contarPromesasActivas = async () => {
  return _feedMemo.run('count:promesas_activas', async () => {
    try {
      const { data, error } = await rpcTribuna('contar_promesas_activas', () => supabase.rpc('contar_promesas_activas'))
      if (error) { console.error('[tribuna] contarPromesasActivas:', error.message); return 0 }
      return Number(data) || 0
    } catch (err) { console.error('[tribuna] contarPromesasActivas timeout:', err?.message || err); return 0 }
  })
}

// Cuenta total de jugadores REGISTRADOS en el producto (criterio del dueño,
// 23-may: "jugador activo" = usuario registrado, NO last_seen 15min).
// Necesita SECURITY DEFINER porque la RLS de `usuarios` solo expone tu fila +
// las de quienes comparten grupo con vos.
export const contarJugadoresRegistrados = async () => {
  return _feedMemo.run('count:jugadores_registrados', async () => {
    try {
      const { data, error } = await rpcTribuna('contar_jugadores_registrados', () => supabase.rpc('contar_jugadores_registrados'))
      if (error) { console.error('[tribuna] contarJugadoresRegistrados:', error.message); return 0 }
      return Number(data) || 0
    } catch (err) { console.error('[tribuna] contarJugadoresRegistrados timeout:', err?.message || err); return 0 }
  })
}

// Cuenta TODAS las reacciones de la comunidad (arenga + cábala + promesa) — el
// total honesto de "movidas tribuneras". Mismo patrón que los otros contadores:
// RPC SECURITY DEFINER porque la RLS de `reacciones_ritual` filtra para anon
// (la Tribuna Pública se ve sin login), así que un SELECT directo daría 0.
export const contarReaccionesTotal = async () => {
  return _feedMemo.run('count:reacciones_total', async () => {
    try {
      const { data, error } = await rpcTribuna('contar_reacciones_total', () => supabase.rpc('contar_reacciones_total'))
      if (error) { console.error('[tribuna] contarReaccionesTotal:', error.message); return 0 }
      return Number(data) || 0
    } catch (err) { console.error('[tribuna] contarReaccionesTotal timeout:', err?.message || err); return 0 }
  })
}

// Cap de escuchas para el ANÓNIMO (CONSTRUIDO APAGADO). El front no puede leer
// config_cobro siendo anon (RLS authenticated-only), así que el estado del flag
// viaja por un RPC SECURITY DEFINER granteado a anon — mismo patrón que los
// contadores. OFF = ausencia de fila en config_cobro → el RPC devuelve
// {enabled:false} vía COALESCE. Error/red caída → degrada a OFF (no rompe la
// tribuna). El LÍMITE no viaja por la base: es constante del front
// (CAP_ARENGAS_ANON_LIMITE en src/utils/cap-arengas-anon.js).
export const getCapArengasAnon = async () => {
  return _feedMemo.run('flag:cap_arengas_anon', async () => {
    try {
      const { data, error } = await rpcTribuna('cap_arengas_anon', () => supabase.rpc('cap_arengas_anon'))
      if (error) { console.error('[tribuna] getCapArengasAnon:', error.message); return { enabled: false } }
      return { enabled: Boolean(data?.enabled) }
    } catch (err) { console.error('[tribuna] getCapArengasAnon timeout:', err?.message || err); return { enabled: false } }
  })
}

// Interruptor "Tribuna Libre" (CONSTRUIDO PRENDIDO). Ventana manual del dueño
// para permitir subir arengas PÚBLICAS sin pacto certificado. El front no puede
// leer config_flags directo (RLS sin policies); el estado viaja por un RPC
// SECURITY DEFINER granteado a anon/authenticated — mismo patrón que
// getCapArengasAnon. El flag SOLO relaja el gate de ESCRITURA (crear_arenga /
// toggle_arenga_visibilidad) server-side; acá se usa únicamente para decidir si
// mostrar el cartel informativo al elegir "Pública". Error/red caída → degrada a
// OFF (no rompe la creación; el servidor sigue siendo la verdad). Ausencia de
// fila en config_flags → el RPC devuelve {enabled:false} vía COALESCE.
export const getTribunaLibre = async () => {
  return _feedMemo.run('flag:tribuna_libre', async () => {
    try {
      const { data, error } = await rpcTribuna('tribuna_libre_activa', () => supabase.rpc('tribuna_libre_activa'))
      if (error) { console.error('[tribuna] getTribunaLibre:', error.message); return { enabled: false } }
      return { enabled: Boolean(data?.enabled) }
    } catch (err) { console.error('[tribuna] getTribunaLibre timeout:', err?.message || err); return { enabled: false } }
  })
}

// ─── Routing del link viral /t/AUDIO_ID (FASE 3.17 · Bloque D) ────────────
import { getArengaPorAudioId } from './rituales.js'

// esArengaAccesible(arenga, currentUser) → { accesible, motivo }.
//   - 'no_existe'      → la arenga no se encontró o RLS la oculta.
//   - 'requiere_login' → arenga privada y no hay sesión.
//   - 'no_sos_miembro' → arenga privada, hay sesión, pero no es miembro.
//   - accesible:true   → puede ver la arenga.
//
// Limitación documentada: para visitante anon o logueado-no-miembro de
// privada, la RLS devuelve null. NO podemos distinguir "no existe" de "es
// privada inaccesible" sin un RPC SECURITY DEFINER. Si arenga=null y no hay
// sesión, asumimos requiere_login (mensaje optimista: "puede ser privada,
// iniciá sesión y volvé a probar"). Si arenga=null y hay sesión, asumimos
// no_existe (el usuario logueado ya tiene la RLS de su lado).
export const esArengaAccesible = (arenga, currentUser) => {
  if (!arenga) {
    if (!currentUser) return { accesible: false, motivo: 'requiere_login' }
    return { accesible: false, motivo: 'no_existe' }
  }
  if (arenga.es_publica) return { accesible: true }
  // arenga privada: si llegó hasta acá es porque la RLS la dejó pasar (el
  // user es miembro). Si no fuera miembro, arenga sería null.
  if (currentUser) return { accesible: true }
  return { accesible: false, motivo: 'requiere_login' }
}

// resolverRutaTribunaPublica(audioId, currentUser) → resuelve el routing del
// link viral. Devuelve { accesible, motivo, arenga }. arenga es el row con
// los campos del SELECT (incluye grupos.code para resolver el codigo del
// grupo activo de UX).
export const resolverRutaTribunaPublica = async (audioId, currentUser) => {
  if (!audioId) return { accesible: false, motivo: 'no_existe', arenga: null }
  const arenga = await getArengaPorAudioId(audioId)
  const { accesible, motivo } = esArengaAccesible(arenga, currentUser)
  return { accesible, motivo, arenga }
}
